import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Database, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { errMsg } from "./ui";
import { PreviewSourcesCard } from "./preview/PreviewSourcesCard";
import { RemovalNoticeCard } from "./preview/RemovalNoticeCard";

/** Billing Sync → Settings (super admin): which MySQL connection + table holds product_map. */
export function BillingSyncSettings() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ connection_id: "", table: "product_map" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/billing-sync/config").then(({ data }) => {
    setCfg(data); setForm({ connection_id: data.connection?.id || "", table: data.table || "product_map" });
  }).catch((e) => toast.error(errMsg(e, "Could not load the settings")));
  useEffect(() => { load(); }, []);
  if (!cfg) return <Loader2 className="mx-auto mt-12 h-6 w-6 animate-spin text-slate-300" />;
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/billing-sync/config", form); toast.success(`Connected — ${data.table} has ${data.columns.length} columns`); load(); }
    catch (e) { toast.error(errMsg(e, "Could not connect")); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-3xl space-y-4" data-testid="bs-settings">
      <Card className="overflow-hidden border-slate-200 shadow-sm">
        <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600"><Database className="h-4 w-4" /></div>
          <div>
            <h3 className="font-heading text-sm font-semibold text-slate-800">Billing database</h3>
            <p className="text-xs text-slate-500">The MySQL database that holds the product-mapping table tracked_items.php reads. Product Mapping reads and writes this table live.</p>
          </div>
        </div>
        <div className="space-y-4 px-5 py-4">
          <label className="block space-y-1"><span className="text-xs font-medium text-slate-600">Connection</span>
            <select value={form.connection_id} onChange={(e) => setForm({ ...form, connection_id: e.target.value })} data-testid="bs-settings-connection"
                    className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
              <option value="">Pick a MySQL connection…</option>
              {(cfg.connections || []).map((c) => <option key={c.id} value={c.id}>{`${c.name} — ${c.host}/${c.database}`}</option>)}
            </select>
            <span className="block text-[11px] text-slate-500">Not listed? Add it under <Link to="/settings?tab=db-connections" className="font-medium text-emerald-700 underline" data-testid="bs-settings-add-connection">Settings → DB Connections</Link> (type MySQL).</span>
          </label>
          <label className="block space-y-1"><span className="text-xs font-medium text-slate-600">Table</span>
            <Input value={form.table} onChange={(e) => setForm({ ...form, table: e.target.value })} className="h-9 w-64 font-mono text-sm" data-testid="bs-settings-table" />
          </label>
          <div className="flex items-center gap-3">
            <Button onClick={save} disabled={busy || !form.connection_id || !form.table.trim()} data-testid="bs-settings-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Test & save</Button>
            {cfg.configured && <span className="text-xs text-slate-500" data-testid="bs-settings-current">Now: <b>{cfg.connection.name}</b> · {cfg.table}{cfg.updated_by ? ` · set by ${cfg.updated_by}` : ""}</span>}
          </div>
        </div>
      </Card>
      <PreviewSourcesCard connections={cfg.connections || []} />
      <RemovalNoticeCard />
    </div>
  );
}
